import { createFileRoute, SearchSchemaInput, useNavigate } from '@tanstack/react-router'
import { useMutation } from '@tanstack/react-query'
import { Api, is_intercity_enabled_account } from '@/lib/utils'
import { useStore } from '@/store'
import { useShallow } from 'zustand/shallow'
import { AddOrderFormFields, Address, Quote } from '@/lib/interfaces'
import { useEffect, useReducer, useState } from 'react'
import { toast } from 'sonner'
import { ShowPriceQuotes } from '@/components/features/orders/show-price-quotes'
import { InsufficentBalanceDialog } from '@/components/features/orders/insufficient-balance'
import AddOrderForm from '@/components/features/orders/add-order-form'

export const Route = createFileRoute('/_auth/orders/add_order')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown> & SearchSchemaInput): { orderId: string } => {
    return {
      orderId: search.orderId as string || ''
    }
  }
})


const initialValue: AddOrderFormFields = {
  storeId: '', billNumber: '', orderAmount: '', pickupOtp: '', cod: false, readyToShip: true, deliveryMode: 'hyperlocal',
  orderWeight: '', length: '', breadth: '', height: '', name: '', phoneNumber: '',
  addressLine1: '', addressLine2: '', city: '', state: '', pincode: '', geolocation: '', dropCode: ''
}

// Intercity rides the ONDC:LOG11 domain, which the buyer API selects off search_category. Hyperlocal
// sends nothing so the backend keeps applying the account's configured category.
const INTERCITY_SEARCH_CATEGORY = 'Standard Delivery'

const packageDetails = (state: AddOrderFormFields, intercityEnabled: boolean) => intercityEnabled && state.deliveryMode === 'intercity' ? {
  search_category: INTERCITY_SEARCH_CATEGORY,
  order_weight: Number(state.orderWeight),
  dimension: {
    length: Number(state.length),
    breadth: Number(state.breadth),
    height: Number(state.height)
  }
} : {}

const reducer = (state: AddOrderFormFields, action: { type: 'reset' | 'update', payload: Partial<AddOrderFormFields> }) => {
  switch (action.type) {
    case "update":
      return { ...state, ...action.payload }
    case "reset":
      return { ...initialValue, ...action.payload }
  }
}

function RouteComponent() {
  const [state, dispatch] = useReducer(reducer, initialValue)
  const [showQuotes, quotesDialogOpen] = useState(false)
  const [showInsufficentBalance, insufficentBalanceDialogOpen] = useState(false)
  const [alertMessage, setAlertMessage] = useState<string | undefined>(undefined)

  const { token, setPage, codEnabled, accountId, phone, email, selectedLspEnabled, isRetail, role } = useStore(useShallow(state => ({
    token: state.token,
    setPage: state.setPage,
    codEnabled: state.selectedAccount.cod_enabled,
    email: state.email,
    phone: state.phoneNumber,
    accountId: state.selectedAccount.id,
    selectedLspEnabled: state.selectedAccount.selected_lsp_enabled,
    isRetail: state.selectedAccount.is_retail,
    role: state.selectedAccount.role
  })))

  const intercityEnabled = is_intercity_enabled_account(accountId)

  useEffect(() => {
    setPage('Orders')
  }, [])

  const { orderId } = Route.useSearch()
  const navigate = useNavigate()

  const createOrder = useMutation({
    mutationFn: (data: { latitude: number, longitude: number, lspId?: string, itemId?: string, quoteId?: string }) => {
      let payload: { [k: string]: string | number | string[] | undefined | { [j: string]: string | undefined | number | Address }, select_criteria: { mode: string, lsp_id?: string, quote_id?: string, item_id?: string } } = {
        ...packageDetails(state, intercityEnabled),
        client_order_id: state.billNumber,
        pickup: {
          otp: state.pickupOtp,
          store_id: state.storeId,
          rto_otp: state.dropCode
        },
        drop: {
          lat: data.latitude,
          lng: data.longitude,
          address: {
            name: state.name,
            line1: state.addressLine1,
            line2: state.addressLine2,
            city: state.city,
            state: state.state,
          },
          pincode: state.pincode,
          phone: state.phoneNumber,
          otp: state.dropCode
        },
        ready_to_ship: state.readyToShip ? "yes" : "no",
        order_amount: state.orderAmount,
        order_items: [],
        select_criteria: {
          mode: "auto_select"
        },
        route: 'webui'
      }

      if (data.lspId && data.itemId && data.quoteId) {
        payload.select_criteria.mode = 'selected_lsp'
        payload.select_criteria.lsp_id = data.lspId
        payload.select_criteria.item_id = data.itemId
        payload.select_criteria.quote_id = data.quoteId
      }

      if (state.cod) {
        payload.cod_amount = state.orderAmount
      }

      return Api<{ status: number, message: string, order?: { id: string }, error_code?: number }>(`/webui/order/${data.lspId ? 'create' : 'createasync'}`, "POST", payload, { token })
    },
    onError(error: { error_code?: number, message: string }, variables) {
      // A quote list is tied to the transaction_id of the search that produced it, so the LSP only
      // accepts one order against it - a second confirm comes back as a duplicate order id. Once
      // Place Order has fired, drop the quotes so the user re-prices instead of retrying a dead one.
      const usedQuotes = !!variables.quoteId
      if (usedQuotes) {
        quotesDialogOpen(false)
        getPriceQuotes.reset()
      }
      if (error.error_code === 2) {
        insufficentBalanceDialogOpen(true)
        setAlertMessage(error.message)
      } else {
        const message = error.message || 'Error creating order'
        toast.error(usedQuotes ? `${message}. Please check price again.` : message)
      }
    },
    onSuccess() {
      toast.success('Order created successfully')
      navigate({ to: '/orders' })
    }
  })

  const getPriceQuotes = useMutation({
    mutationFn: (data: {
      storeDetails: { lat: number, lng: number, pin: string, city: string },
      drop: { lat: number, lng: number, pin: string }
    }) => {
      return Api<{ status: number, valid_until: string, quote_id: string, quotes: Quote[] }>('/webui/quotes', "POST",
        {
          pickup: {
            lat: data.storeDetails.lat,
            lng: data.storeDetails.lng,
            pincode: data.storeDetails.pin
          },
          drop: {
            lat: data.drop.lat,
            lng: data.drop.lng,
            pincode: data.drop.pin
          },
          city: data.storeDetails.city,
          order_amount: state.orderAmount,
          cod_amount: state.cod ? state.orderAmount : undefined,
          ...packageDetails(state, intercityEnabled)
        }, { token })
    },
    onError(error) {
      toast.error(error.message ||  "Error getting price quotes")
    },
    onSuccess() {
      quotesDialogOpen(true)
    }
  })

  return <div className='fixed top-18 bottom-5 left-1 right-1 flex justify-center items-center'>
    <AddOrderForm createOrder={() => {
      const match = state.geolocation.match(/(\d+\.\d+)\s*,\s*(\d+\.\d+)/)
      if (match) {
        createOrder.mutate({ latitude: parseFloat(match[1]), longitude: parseFloat(match[2]) })
      }
    }} token={token} orderId={orderId} formData={state} updateField={f => dispatch({ type: 'update', payload: f })} codEnabled={codEnabled} getPriceQuotes={(store) => {
      const match = state.geolocation.match(/(\d+\.\d+)\s*,\s*(\d+\.\d+)/)
      if (match) {
        getPriceQuotes.mutate({
          storeDetails: {
            lat: store.latitude,
            lng: store.longitude,
            pin: store.pincode,
            city: store.address.city
          },
          drop: {
            lat: parseFloat(match[1]),
            lng: parseFloat(match[2]),
            pin: state.pincode
          },
        })
      }
    }} loadingPriceQuotes={getPriceQuotes.isPending} isRetail={isRetail} role={role}/>
    <ShowPriceQuotes onClose={() => quotesDialogOpen(false)} open={showQuotes} quotes={getPriceQuotes.data?.quotes || []} loading={createOrder.isPending}
      placeOrder={selectedLspEnabled && isRetail ? (lspId, itemId) => {
        const match = state.geolocation.match(/(\d+\.\d+)\s*,\s*(\d+\.\d+)/)
        if (match) {
          createOrder.mutate({ latitude: parseFloat(match[1]), longitude: parseFloat(match[2]), lspId, itemId, quoteId: getPriceQuotes.data?.quote_id })
        }
      } : undefined} />
    <InsufficentBalanceDialog open={showInsufficentBalance} onClose={() => insufficentBalanceDialogOpen(false)} accountId={accountId} phone={phone} email={email} message={alertMessage} />
  </div>
}
